import { useMemo } from 'react';
import { Activity, ArrowDownRight, ArrowUpRight, Crown, TrendingUp } from 'lucide-react';
import { SectionTitle } from '@/components/design-system';
import { useLeadsMarketing } from '@/hooks/queries/useLeadsMarketing';
import type { BoosterMarketingRow, CampagneBoostRow, PageMarketingRow } from '@/lib/database.types';
import { formatDA } from '@/lib/format';

// Sprint 10.0 — KPIs marketing avancés (calcul en mémoire, pas de migration).
// Sources :
//   - leads_marketing (par page, par statut)
//   - campagnes_boost (montant + nb_messages_recus + nb_inscrits)
//   - boosters_marketing (mapping nom)

interface Props {
  entrepriseId: string;
  pages: PageMarketingRow[];
  boosters: BoosterMarketingRow[];
  campagnes: CampagneBoostRow[];
}

export function PerformanceTab({ entrepriseId, pages, boosters, campagnes }: Props): JSX.Element {
  const { data: leads = [] } = useLeadsMarketing({ entrepriseId });

  // ===== KPIs par page =====
  const perfParPage = useMemo(() => {
    return pages.map((p) => {
      const leadsPage = leads.filter((l) => l.source_page_id === p.id);
      const inscrits = leadsPage.filter((l) => l.statut === 'inscrit').length;
      const total = leadsPage.length;
      const tauxConv = total > 0 ? (inscrits / total) * 100 : 0;
      return {
        page: p,
        nbLeads: total,
        nbInscrits: inscrits,
        tauxConv,
      };
    });
  }, [pages, leads]);

  // ===== KPIs par campagne (CPL, CR) =====
  const perfParCampagne = useMemo(() => {
    return campagnes.map((c) => {
      const cpl = c.nb_messages_recus > 0 ? c.montant / c.nb_messages_recus : 0;
      const cpInscrit = c.nb_inscrits > 0 ? c.montant / c.nb_inscrits : 0;
      const cr = c.nb_messages_recus > 0 ? (c.nb_inscrits / c.nb_messages_recus) * 100 : 0;
      return { campagne: c, cpl, cpInscrit, cr };
    });
  }, [campagnes]);

  // ===== Top boosters par nb_inscrits cumulés =====
  const topBoosters = useMemo(() => {
    const totals = new Map<
      string,
      {
        booster: BoosterMarketingRow;
        budget: number;
        inscrits: number;
        messages: number;
        nbCampagnes: number;
      }
    >();
    for (const b of boosters) {
      totals.set(b.id, { booster: b, budget: 0, inscrits: 0, messages: 0, nbCampagnes: 0 });
    }
    for (const c of campagnes) {
      if (!c.booster_id) continue;
      const cur = totals.get(c.booster_id);
      if (!cur) continue;
      cur.budget += Number(c.montant);
      cur.inscrits += c.nb_inscrits;
      cur.messages += c.nb_messages_recus;
      cur.nbCampagnes += 1;
    }
    return [...totals.values()]
      .filter((b) => b.nbCampagnes > 0)
      .sort((a, b) => b.inscrits - a.inscrits);
  }, [boosters, campagnes]);

  return (
    <div className="space-y-6">
      <section>
        <SectionTitle>
          <span className="inline-flex items-center gap-2">
            <Activity className="h-4 w-4 text-blue-600" /> Performance par page
          </span>
        </SectionTitle>
        {perfParPage.length === 0 ? (
          <p className="mt-3 text-sm text-muted-foreground">Aucune page enregistrée.</p>
        ) : (
          <div className="mt-3 overflow-hidden rounded-xl border border-slate-200 bg-white">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-[10px] font-bold uppercase text-slate-600">
                <tr>
                  <th className="px-3 py-2 text-left">Page</th>
                  <th className="px-3 py-2 text-right">Leads</th>
                  <th className="px-3 py-2 text-right">Inscrits</th>
                  <th className="px-3 py-2 text-right">Taux conversion</th>
                </tr>
              </thead>
              <tbody>
                {perfParPage.map((r) => (
                  <tr key={r.page.id} className="border-t border-slate-100">
                    <td className="px-3 py-2">
                      <span className="text-[10px] uppercase text-slate-500">
                        {r.page.plateforme}
                      </span>{' '}
                      <span className="font-medium">{r.page.nom}</span>
                    </td>
                    <td className="num px-3 py-2 text-right">{r.nbLeads}</td>
                    <td className="num px-3 py-2 text-right font-semibold text-emerald-700">
                      {r.nbInscrits}
                    </td>
                    <td className="num px-3 py-2 text-right">
                      {r.nbLeads === 0 ? (
                        <span className="text-muted-foreground">—</span>
                      ) : (
                        <span
                          className={
                            r.tauxConv >= 20
                              ? 'inline-flex items-center gap-0.5 font-bold text-emerald-700'
                              : r.tauxConv >= 10
                                ? 'font-semibold text-amber-700'
                                : 'text-rose-700'
                          }
                        >
                          {r.tauxConv >= 20 && <ArrowUpRight className="h-3 w-3" />}
                          {r.tauxConv >= 10 && r.tauxConv < 20 && (
                            <TrendingUp className="h-3 w-3" />
                          )}
                          {r.tauxConv < 10 && <ArrowDownRight className="h-3 w-3" />}
                          {r.tauxConv.toFixed(1)}%
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="mt-2 text-[10px] text-muted-foreground">
          Conversion calculée sur les leads marqués <strong>inscrit</strong>. Couleurs : vert ≥ 20
          %, ambre ≥ 10 %, rouge sinon.
        </p>
      </section>

      <section>
        <SectionTitle>
          <span className="inline-flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-violet-600" /> Performance par campagne (CPL / CR)
          </span>
        </SectionTitle>
        {perfParCampagne.length === 0 ? (
          <p className="mt-3 text-sm text-muted-foreground">Aucune campagne lancée.</p>
        ) : (
          <div className="mt-3 overflow-x-auto rounded-xl border border-slate-200 bg-white">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-[10px] font-bold uppercase text-slate-600">
                <tr>
                  <th className="px-3 py-2 text-left">Réf</th>
                  <th className="px-3 py-2 text-right">Budget</th>
                  <th className="px-3 py-2 text-right">Messages</th>
                  <th className="px-3 py-2 text-right">Inscrits</th>
                  <th className="px-3 py-2 text-right">CPL</th>
                  <th className="px-3 py-2 text-right">Coût / inscrit</th>
                  <th className="px-3 py-2 text-right">Conversion</th>
                </tr>
              </thead>
              <tbody>
                {perfParCampagne.map((r) => (
                  <tr key={r.campagne.id} className="border-t border-slate-100">
                    <td className="px-3 py-2 font-mono text-[10px]">{r.campagne.reference}</td>
                    <td className="num px-3 py-2 text-right">
                      {formatDA(Number(r.campagne.montant))}
                    </td>
                    <td className="num px-3 py-2 text-right">{r.campagne.nb_messages_recus}</td>
                    <td className="num px-3 py-2 text-right font-semibold text-emerald-700">
                      {r.campagne.nb_inscrits}
                    </td>
                    <td className="num px-3 py-2 text-right text-slate-600">
                      {r.campagne.nb_messages_recus > 0 ? formatDA(Math.round(r.cpl)) : '—'}
                    </td>
                    <td className="num px-3 py-2 text-right font-semibold">
                      {r.campagne.nb_inscrits > 0 ? formatDA(Math.round(r.cpInscrit)) : '—'}
                    </td>
                    <td className="num px-3 py-2 text-right">
                      {r.campagne.nb_messages_recus === 0 ? (
                        <span className="text-muted-foreground">—</span>
                      ) : (
                        <span
                          className={
                            r.cr >= 20
                              ? 'font-bold text-emerald-700'
                              : r.cr >= 10
                                ? 'font-semibold text-amber-700'
                                : 'text-rose-700'
                          }
                        >
                          {r.cr.toFixed(1)}%
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="mt-2 text-[10px] text-muted-foreground">
          <strong>CPL</strong> = budget / nombre messages reçus · <strong>Coût/inscrit</strong> =
          budget / nombre conversions · <strong>Conversion</strong> = inscrits / messages.
        </p>
      </section>

      <section>
        <SectionTitle>
          <span className="inline-flex items-center gap-2">
            <Crown className="h-4 w-4 text-amber-600" /> Top boosters
          </span>
        </SectionTitle>
        {topBoosters.length === 0 ? (
          <p className="mt-3 text-sm text-muted-foreground">
            Aucun booster actif sur une campagne.
          </p>
        ) : (
          <div className="mt-3 overflow-hidden rounded-xl border border-amber-200 bg-amber-50/30">
            <table className="w-full text-xs">
              <thead className="bg-amber-100/40 text-[10px] font-bold uppercase text-amber-900">
                <tr>
                  <th className="px-3 py-2 text-left">#</th>
                  <th className="px-3 py-2 text-left">Booster</th>
                  <th className="px-3 py-2 text-right">Campagnes</th>
                  <th className="px-3 py-2 text-right">Budget cumulé</th>
                  <th className="px-3 py-2 text-right">Messages</th>
                  <th className="px-3 py-2 text-right">Inscrits</th>
                  <th className="px-3 py-2 text-right">Coût / inscrit</th>
                </tr>
              </thead>
              <tbody>
                {topBoosters.map((r, i) => {
                  const cpInscrit = r.inscrits > 0 ? r.budget / r.inscrits : 0;
                  return (
                    <tr key={r.booster.id} className="border-t border-amber-200/50">
                      <td className="px-3 py-2 font-bold text-amber-700">
                        {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${i + 1}`}
                      </td>
                      <td className="px-3 py-2 font-medium">
                        {r.booster.nom}{' '}
                        <span className="text-[10px] text-muted-foreground">
                          ({r.booster.type})
                        </span>
                      </td>
                      <td className="num px-3 py-2 text-right">{r.nbCampagnes}</td>
                      <td className="num px-3 py-2 text-right">{formatDA(r.budget)}</td>
                      <td className="num px-3 py-2 text-right">{r.messages}</td>
                      <td className="num px-3 py-2 text-right font-semibold text-emerald-700">
                        {r.inscrits}
                      </td>
                      <td className="num px-3 py-2 text-right">
                        {r.inscrits > 0 ? formatDA(Math.round(cpInscrit)) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <p className="mt-2 text-[10px] text-muted-foreground">
          Classement par nombre d&apos;inscriptions converties. Le coût/inscrit le plus bas indique
          le booster le plus efficace.
        </p>
      </section>
    </div>
  );
}
